import { forwardRef, Link, LinkProps as ChakraLinkProps } from '@chakra-ui/react';
import type { ControlDrawerOrigin } from '@main/shared/utils';
import { Link as RouterLink, LinkProps } from '@tanstack/react-router';
import { ReactNode } from 'react';

import { DrawerEntity } from '../drawer';
import { DefaultTabNameSpace, TabId } from '../tabs';

export type NavigateToDrawer = {
  drawerEntityId: string;
  activeTab?: TabId;
} & (
  | { drawerEntity: 'control'; drawerOrigin: ControlDrawerOrigin }
  | { drawerEntity: Exclude<DrawerEntity, 'control'>; drawerOrigin?: never }
);

export type NavigateToRoute = Omit<LinkProps, 'children'> & { ariaLabel?: string };

export type NavigateTarget = NavigateToDrawer | NavigateToRoute;

type NavigateLinkProps = Omit<ChakraLinkProps, 'as' | 'to' | 'href' | 'search'> & {
  navigate: NavigateTarget;
  children: ReactNode;
};

/**
 * Router link for drawer search params or a concrete route.
 * Same navigate shape as {@link ReferenceCard}.
 */
export const NavigateLink = forwardRef<NavigateLinkProps, 'a'>(
  ({ navigate, children, ...linkProps }, ref) => {
    if ('drawerEntity' in navigate) {
      return (
        <Link
          ref={ref}
          as={RouterLink}
          to="."
          search={(prev: Record<string, unknown>) => ({
            ...prev,
            ...navigate,
            drawerOrigin: navigate.drawerEntity === 'control' ? navigate.drawerOrigin : undefined,
            [DefaultTabNameSpace]: navigate.activeTab ?? undefined,
          })}
          {...linkProps}
        >
          {children}
        </Link>
      );
    }

    const { ariaLabel, ...routeProps } = navigate;
    return (
      <Link ref={ref} as={RouterLink} aria-label={ariaLabel} {...routeProps} {...linkProps}>
        {children}
      </Link>
    );
  },
);
